import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useWindowOn } from 'react-use-rect';
import type { Place } from '../../../../../types';
import { getWindowHeight, useIsOutOfViewport } from '../../../../helpers';
import { useTheme } from '../../../common';
import { usePlayer } from '../../PlayerProvider';
import {
  CloseButton,
  IconComponent,
  Loop,
  Next,
  PlaylistButton,
  PlayPause,
  Prev,
  Shuffle,
  VolumeComponent,
  VolumeControlButtonComponent,
  VolumeControlComponent,
  VolumeControlSliderComponent
} from '../../player-parts';
import { Playlist } from '../../playlist';

export function Background() {
  const { showPlayer } = usePlayer();
  const {
    playerControls,
    playlistHeight: settingsPlaylistHeight,
    closeButton,
    layout,
    position
  } = useSettings();
  const { textColor, backgroundStyle } = useTheme();
  const [playlistPosition, setPlaylistPosition] = useState<Place[]>([
    'top',
    'left'
  ]);
  const isFloatingLayout = layout.includes('floating');

  const {
    out,
    ref: playlistRef,
    containerHeight,
    containerRect,
    revalidate
  } = useIsOutOfViewport();
  const [playlistHeight, setPlaylistHeight] = useState(0);

  const calculatedMaxHeight = useMemo(() => {
    const playlistHeightString = settingsPlaylistHeight
      .toString()
      .toLowerCase();
    const playlistHeightNumber = parseInt(playlistHeightString, 10);

    const isAutoHeight = ['100%', 'auto'].includes(playlistHeightString);

    if (isAutoHeight) {
      return playlistHeight;
    }

    if (isFloatingLayout) {
      return Math.min(playlistHeightNumber, playlistHeight);
    }

    return playlistHeightNumber;
  }, [settingsPlaylistHeight, playlistHeight, isFloatingLayout]);

  const controlsLength = playerControls.length + Number(closeButton);

  const showMainControls =
    playerControls.includes('prevNext') || playerControls.includes('play');

  const showSecondControls =
    playerControls.includes('shuffle') ||
    playerControls.includes('loop') ||
    playerControls.includes('volume') ||
    playerControls.includes('playlist') ||
    closeButton;

  useEffect(() => {
    if (out.top) {
      setPlaylistPosition((previousPosition) => [
        'bottom',
        previousPosition[1]
      ]);
    } else {
      setPlaylistPosition((previousPosition) => ['top', previousPosition[1]]);
    }

    let newDirection;
    const prefersRight = position.toLowerCase().includes('right');
    if (prefersRight) {
      if (out.right) {
        newDirection = 'left';
      } else {
        newDirection = 'right';
      }
    } else {
      if (out.left) {
        newDirection = 'right';
      } else {
        newDirection = 'left';
      }
    }

    setPlaylistPosition((previousPosition) => [
      previousPosition[0],
      newDirection as Place
    ]);
  }, [out, position]);

  useEffect(() => {
    const newHeight = getWindowHeight() - containerHeight - 48;
    setPlaylistHeight(newHeight);
  }, [containerHeight]);

  useWindowOn('resize', () => revalidate({ force: true }));

  if (!showPlayer) {
    return null;
  }

  return (
    <Component
      ref={containerRect}
      _backgroundStyle={backgroundStyle}
      _circle={controlsLength === 1 && !playerControls.includes('prevNext')}
      _hasControls={!!controlsLength}
    >
      <Controls>
        {showMainControls && (
          <ControlsMain>
            {playerControls.includes('prevNext') && <Prev />}
            {playerControls.includes('play') && <PlayPause />}
            {playerControls.includes('prevNext') && <Next />}
          </ControlsMain>
        )}

        {showSecondControls && (
          <ControlsSecond>
            {playerControls.includes('shuffle') && <Shuffle />}
            {playerControls.includes('loop') && <Loop />}

            {playerControls.includes('volume') && <VolumeComponent />}
            {playerControls.includes('playlist') && <PlaylistButton />}
            {closeButton && isFloatingLayout && <CloseButton />}
          </ControlsSecond>
        )}
      </Controls>

      {layout === 'floatingBackground' && (
        <StyledPlaylist
          ref={playlistRef}
          maxHeight={calculatedMaxHeight}
          _playlistPosition={playlistPosition}
          _textColor={textColor}
          _hasControls={!!controlsLength}
        />
      )}
    </Component>
  );
}

const Component = styled.div<{
  _circle: boolean;
  _backgroundStyle: string;
  _hasControls: boolean;
}>`
  display: inline-flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  backface-visibility: hidden;
  border-radius: ${({ _circle }) => (_circle ? '50%' : '8px')};
  ${({ _backgroundStyle }) => _backgroundStyle}

  ${({ _hasControls, _circle }) =>
    _hasControls &&
    css`
      height: 60px;
      padding: 18px ${_circle ? '12px' : '18px'};
    `}

  ${({ _circle }) =>
    _circle &&
    css`
      width: 60px;
      justify-content: center;
    `}

  & * {
    box-sizing: border-box;
  }

  & > div > div > div {
    width: ${({ _circle }) => (_circle ? '36px' : '24px')};
    height: ${({ _circle }) => (_circle ? '36px' : '24px')};
  }

  & ${VolumeControlSliderComponent} {
    right: 50%;
    transform: translateX(50%);
  }

  ${({ _circle }) =>
    _circle &&
    css`
      & ${IconComponent} {
        display: inline-flex;
      }

      & ${Controls} > div {
        justify-content: center;
      }

      & ${VolumeControlButtonComponent} > div {
        display: inline-flex;
      }
    `}

  & ${VolumeControlButtonComponent} {
    width: ${({ _circle }) => (_circle ? '36px' : '24px')};
    height: ${({ _circle }) => (_circle ? '36px' : '24px')};
    flex-basis: ${({ _circle }) => (_circle ? '36px' : '24px')};
  }

  &:before {
    border-radius: ${({ _circle }) => (_circle ? '50%' : '8px')};
  }
`;

const Controls = styled.div`
  display: inline-flex;
  align-items: center;

  & > div:first-child {
    margin-left: 0;
  }
`;

const ControlsMain = styled.div`
  display: inline-flex;
  align-items: center;

  & > div:first-child {
    margin-left: 0;
  }
`;

const ControlsSecond = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: 40px;

  &:first-child {
    margin-left: 0;
  }

  & > div:first-child {
    margin-left: 0;
  }

  & ${VolumeControlComponent} {
    margin-left: 16px;
  }
`;

const StyledPlaylist = styled(Playlist)<{
  _playlistPosition: Place[];
  _textColor: string;
  _hasControls: boolean;
}>`
  width: 280px;
  position: absolute;
  box-shadow: 0 0 10px 0 rgba(17, 17, 17, 0.2);
  border-radius: 8px;
  padding: 8px;
  ${({ _hasControls }) =>
    _hasControls &&
    css`
      margin: 12px 0;
    `}
  ${({ _playlistPosition }) => `
    top: ${_playlistPosition[0] === 'bottom' ? '100%' : 'auto'};
    right: ${_playlistPosition[1] === 'right' ? '0' : 'auto'};
    bottom: ${_playlistPosition[0] === 'top' ? '100%' : 'auto'};
    left: ${
      _playlistPosition && _playlistPosition[1] === 'left' ? '0' : 'auto'
    };
  `}
  color: ${({ _textColor }) => _textColor};
  background-color: ${({ _textColor }) => getInvertedColor(_textColor)};
`;
